import { AmplitudeEvent } from "@common-shared/config/amplitude.config.ts"
import {
    PoliciesListType,
    PolicyAssignmentPriority,
    PolicyAssignmentStatus,
    PolicyReassignmentReason,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import type { IPagination } from "@common-shared/types/general.types.ts"
import { ICoverageHistoryListSort } from "@common-shared/types/internalApis/coverageHistory.types.ts"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types.ts"
import {
    IOneVerifyCreatePolicyAssignmentData,
    IOneVerifyGetAssignmentsProductivityExportCountRequestData,
    IOneVerifyGetAssignmentsProductivityExportRequestData,
    IOneVerifyGetHistoryListAdditionalData,
    IOneVerifyReassignPolicyRequestData,
    IOneVerifyUpdatePolicyAssignmentPriorityData,
    IOneVerifyUpdatePolicyAssignmentStatusData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types.ts"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"
import {
    IOneVerifyAssignmentsProductivityExportChunkResponse,
    IOneVerifyAssignmentsProductivityExportCountResponse,
    IOneVerifyCoverageCheckHistory,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { API_ROUTES } from "../config/routes.config"
import { AmplitudeHelper } from "../helpers/amplitude.helper.tsx"
import { HttpHelper } from "../helpers/http.helper"
import { PoliciesHelper } from "../helpers/policies.helper.ts"
import { UtilHelper } from "../helpers/util.helper.tsx"
import type { ICoverageHistoryListResult } from "../types/data.types.ts"
import { IAssignmentIdentification } from "../types/objects.types.ts"
import { PoliciesService } from "./policies.service.ts"

export class TaskAssignmentsService {
    /**
     * Get list of list of task assignments
     */
    static async getTaskAssignments(
        practice: IPractice,
        listType: PoliciesListType,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.POLICY_ASSIGNMENT_PAGE_VIEWED, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: listType,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.COVERAGE_TASK_ASSIGNMENT_GET_LIST,
            practice,
            PoliciesService.mapGetHistoryListRequestData(
                listType,
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Create task assignment
     */
    static async createTaskAssignment(
        practice: IPractice,
        nirvanaRequestId: string,
        assigneeId: number | string,
        priority?: PolicyAssignmentPriority,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const requestData: IOneVerifyCreatePolicyAssignmentData = {
            nirvanaRequestId,
            assigneeUserId: +assigneeId,
            priority,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.TASK_ASSIGNMENTS_CREATE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            requestData,
            "POST",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Update task assignment status
     */
    static async updateTaskAssignmentStatus(
        practice: IPractice,
        assignment: IAssignmentIdentification,
        status: PolicyAssignmentStatus,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const { id, nirvanaRequestId } = assignment

        const requestData: IOneVerifyUpdatePolicyAssignmentStatusData = {
            nirvanaRequestId,
            status,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.TASK_ASSIGNMENTS_UPDATE_STATUS.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":assignmentId", id),
            requestData,
            "PUT",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Update task assignment priority
     */
    static async updateTaskAssignmentPriority(
        practice: IPractice,
        assignment: IAssignmentIdentification,
        priority: PolicyAssignmentPriority,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const { id, nirvanaRequestId } = assignment

        const requestData: IOneVerifyUpdatePolicyAssignmentPriorityData = {
            nirvanaRequestId,
            priority,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.TASK_ASSIGNMENTS_UPDATE_PRIORITY.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":assignmentId", id),
            requestData,
            "PUT",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Reassign task assignment
     */
    static async reassignTaskAssignment(
        practice: IPractice,
        assignment: IAssignmentIdentification,
        reassignmentReason?: PolicyReassignmentReason,
        assigneeId?: string | number,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const { id, nirvanaRequestId } = assignment

        const requestData: IOneVerifyReassignPolicyRequestData = {
            nirvanaRequestId,
            reassignmentReason,
            // If assigneeId is not provided, it means unassign
            assigneeUserId:
                typeof assigneeId !== "undefined" ? +assigneeId : undefined,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.TASK_ASSIGNMENTS_REASSIGN.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":assignmentId", id),
            requestData,
            "PUT",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get assignments productivity export
     */
    static async getTaskAssignmentsProductivityExport(
        practice: IPractice,
        data: IOneVerifyGetAssignmentsProductivityExportRequestData,
    ): Promise<
        IOneVerifyAssignmentsProductivityExportChunkResponse | undefined
    > {
        const requestData = {
            ...data,
            // GET query params cannot carry nested objects directly
            dateAssigned: data.dateAssigned
                ? JSON.stringify(data.dateAssigned)
                : undefined,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.TASK_ASSIGNMENTS_PRODUCTIVITY_EXPORT.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            requestData,
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get assignments productivity export total count
     */
    static async getTaskAssignmentsProductivityExportCount(
        practice: IPractice,
        data: IOneVerifyGetAssignmentsProductivityExportCountRequestData,
    ): Promise<
        IOneVerifyAssignmentsProductivityExportCountResponse | undefined
    > {
        // GET query params cannot carry nested objects directly, so we stringify dateAssigned
        const requestData = {
            dateAssigned: JSON.stringify(data.dateAssigned),
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.TASK_ASSIGNMENTS_PRODUCTIVITY_EXPORT_COUNT.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            requestData,
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }
}
